import { useParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CalendarDays, CalendarPlus, Clock, FileText, FolderOpen, Loader2, Pencil, Phone, Upload,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  ORIGEM_LABEL, SITUACAO_CONSULTA_LABEL, TIPO_CONSULTA_LABEL,
  type Clinica, type TimelineEvento,
} from '@/lib/types'
import { formatarData, hora } from '@/lib/datas'
import { listClinicas } from '@/services/clinicas'
import { getPaciente, type PacienteComClinicas } from '@/services/pacientes'
import { listConsultasDoPaciente, type ConsultaComPaciente } from '@/services/consultas'
import { listTimeline } from '@/services/timeline'
import {
  listDocumentos, mudarStatusDocumento, uploadDocumento, urlAssinada,
  STATUS_DOC_LABEL, TIPO_DOC_LABEL, type Documento, type TipoDocumento,
} from '@/services/documentos'
import { listExamesDoPaciente, STATUS_EXAME_LABEL, type ExameSolicitado } from '@/services/exames'
import { criarTarefa } from '@/services/tarefas'

type Aba = 'timeline' | 'consultas' | 'documentos' | 'dados'

export default function PacienteDetalhePage() {
  const { id } = useParams<{ id: string }>()
  const { current, user } = useAuth()
  const [paciente, setPaciente] = useState<PacienteComClinicas | null>(null)
  const [clinicas, setClinicas] = useState<Clinica[]>([])
  const [timeline, setTimeline] = useState<TimelineEvento[]>([])
  const [consultas, setConsultas] = useState<ConsultaComPaciente[]>([])
  const [documentos, setDocumentos] = useState<Documento[]>([])
  const [exames, setExames] = useState<ExameSolicitado[]>([])
  const [aba, setAba] = useState<Aba>('timeline')
  const [loading, setLoading] = useState(true)
  const [busyDoc, setBusyDoc] = useState(false)
  const [tipoUpload, setTipoUpload] = useState<TipoDocumento>('exame')

  const carregar = useCallback(async () => {
    if (!current || !id) return
    setLoading(true)
    try {
      const [p, cs, tl, cons] = await Promise.all([
        getPaciente(current.workspace.id, id),
        listClinicas(current.workspace.id),
        listTimeline(current.workspace.id, id),
        listConsultasDoPaciente(current.workspace.id, id),
      ])
      setPaciente(p)
      setClinicas(cs)
      setTimeline(tl)
      setConsultas(cons)
      try {
        const [docs, exs] = await Promise.all([
          listDocumentos(current.workspace.id, id),
          listExamesDoPaciente(current.workspace.id, id),
        ])
        setDocumentos(docs)
        setExames(exs)
      } catch { /* phase5 ainda não aplicada */ }
    } finally {
      setLoading(false)
    }
  }, [current, id])

  useEffect(() => { carregar() }, [carregar])

  if (!id) return null

  if (loading) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    )
  }

  if (!paciente) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
        Paciente não encontrado (ou fora do seu escopo de acesso).
      </div>
    )
  }

  const nomeClinica = (cid: string) => clinicas.find(c => c.id === cid)?.nome

  return (
    <div>
      <header className="card" style={{ padding: 18, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {paciente.nome}
              {paciente.situacao === 'inativa' && <span className="badge badge-muted">Inativa</span>}
            </h1>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span>{ORIGEM_LABEL[paciente.origem]}</span>
              {paciente.data_nasc && <span>· Nasc. {formatarData(paciente.data_nasc)}</span>}
              {paciente.telefone && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  · <Phone size={12} aria-hidden /> {paciente.telefone}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {paciente.clinica_ids.map(cid => {
                const n = nomeClinica(cid)
                return n ? <span key={cid} className="badge badge-info">{n}</span> : null
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to={`/agenda/nova?paciente=${paciente.id}`} className="btn-primary" style={{ minHeight: 40, fontSize: 13.5 }}>
              <CalendarPlus size={15} aria-hidden /> Agendar
            </Link>
            <Link to={`/pacientes/novo?id=${paciente.id}`} className="btn-secondary" style={{ minHeight: 40, fontSize: 13.5 }}>
              <Pencil size={15} aria-hidden /> Editar
            </Link>
          </div>
        </div>
        {paciente.proxima_acao && (
          <div className="badge badge-warn" style={{ marginTop: 12, fontSize: 13 }}>
            Próxima ação: {paciente.proxima_acao}
          </div>
        )}
      </header>

      <div role="tablist" aria-label="Seções do paciente" style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {([
          ['timeline', 'Timeline'],
          ['consultas', `Consultas (${consultas.length})`],
          ['documentos', `Documentos (${documentos.length})`],
          ['dados', 'Dados'],
        ] as [Aba, string][]).map(([key, label]) => (
          <button
            key={key} role="tab" aria-selected={aba === key}
            onClick={() => setAba(key)}
            className="badge"
            style={{
              cursor: 'pointer', fontSize: 13.5, padding: '8px 14px', border: '1px solid',
              background: aba === key ? 'var(--primary-soft)' : 'var(--card)',
              color: aba === key ? 'var(--primary-deep)' : 'var(--text-secondary)',
              borderColor: aba === key ? 'var(--primary)' : 'var(--border)',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {aba === 'timeline' && (
        timeline.length === 0 ? (
          <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--muted)' }}>
            <Clock size={24} aria-hidden style={{ marginBottom: 8 }} />
            <div>Nenhum evento registrado ainda.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {timeline.map((ev, i) => (
              <div key={ev.id} style={{ display: 'flex', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <span aria-hidden style={{ width: 10, height: 10, borderRadius: 999, background: 'var(--primary)', marginTop: 6, flexShrink: 0 }} />
                  {i < timeline.length - 1 && <span aria-hidden style={{ width: 2, flex: 1, background: 'var(--border)' }} />}
                </div>
                <div className="card" style={{ padding: '12px 14px', marginBottom: 10, flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{ev.titulo}</div>
                  {ev.descricao && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{ev.descricao}</div>}
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                    {new Date(ev.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {aba === 'consultas' && (
        consultas.length === 0 ? (
          <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--muted)' }}>
            <CalendarDays size={24} aria-hidden style={{ marginBottom: 8 }} />
            <div>Nenhuma consulta registrada.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {consultas.map(c => (
              <Link key={c.id} to={`/agenda/nova?id=${c.id}`} className="card" style={{ padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {formatarData(c.data)} às {hora(c.hora_inicio)}
                    {c.clinica_id && nomeClinica(c.clinica_id) ? ` · ${nomeClinica(c.clinica_id)}` : ''}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                    {TIPO_CONSULTA_LABEL[c.tipo]}{c.motivo ? ` · ${c.motivo}` : ''}
                  </div>
                </div>
                <span className={`badge ${c.situacao === 'realizada' ? 'badge-ok' : c.situacao === 'cancelada' || c.situacao === 'faltou' ? 'badge-danger' : 'badge-info'}`}>
                  {SITUACAO_CONSULTA_LABEL[c.situacao]}
                </span>
              </Link>
            ))}
          </div>
        )
      )}

      {aba === 'documentos' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="card" style={{ padding: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              className="field-input" style={{ width: 'auto', minHeight: 40, fontSize: 13.5 }}
              value={tipoUpload} onChange={e => setTipoUpload(e.target.value as TipoDocumento)}
              aria-label="Tipo do documento"
            >
              {(Object.keys(TIPO_DOC_LABEL) as TipoDocumento[]).map(t => (
                <option key={t} value={t}>{TIPO_DOC_LABEL[t]}</option>
              ))}
            </select>
            <label className="btn-secondary" style={{ cursor: 'pointer' }}>
              <Upload size={15} aria-hidden /> {busyDoc ? 'Enviando…' : 'Enviar arquivo'}
              <input
                type="file" style={{ display: 'none' }}
                accept="image/*,.pdf,.doc,.docx"
                disabled={busyDoc}
                onChange={async e => {
                  const f = e.target.files?.[0]
                  if (!f || !current) return
                  setBusyDoc(true)
                  try {
                    await uploadDocumento({ workspaceId: current.workspace.id, pacienteId: id, tipo: tipoUpload, file: f })
                    await carregar()
                  } finally {
                    setBusyDoc(false)
                  }
                }}
              />
            </label>
          </div>

          {documentos.length === 0 ? (
            <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--muted)' }}>
              <FolderOpen size={24} aria-hidden style={{ marginBottom: 8 }} />
              <div>Nenhum documento ainda.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {documentos.map(d => (
                <div key={d.id} className="card" style={{ padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{d.nome}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                        {TIPO_DOC_LABEL[d.tipo]} · {d.origem === 'paciente' ? 'enviado pela paciente' : 'equipe'}
                        {' · '}{new Date(d.created_at).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                    <span className={`badge ${d.status === 'analisado' ? 'badge-ok' : d.status === 'invalido' ? 'badge-danger' : 'badge-warn'}`}>
                      {STATUS_DOC_LABEL[d.status]}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    <button
                      className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                      onClick={async () => {
                        if (!current) return
                        const url = await urlAssinada(current.workspace.id, d)
                        window.open(url, '_blank', 'noopener')
                      }}
                    >
                      Ver / baixar
                    </button>
                    {d.status !== 'analisado' && (
                      <button
                        className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                        onClick={async () => {
                          if (!current) return
                          await mudarStatusDocumento({ workspaceId: current.workspace.id, doc: d, status: 'analisado' })
                          await carregar()
                        }}
                      >
                        Marcar analisado
                      </button>
                    )}
                    {d.status !== 'invalido' && d.status !== 'analisado' && (
                      <button
                        className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                        onClick={async () => {
                          if (!current) return
                          await mudarStatusDocumento({ workspaceId: current.workspace.id, doc: d, status: 'invalido' })
                          await carregar()
                        }}
                      >
                        Inválido
                      </button>
                    )}
                    {d.status !== 'analisado' && (
                      <button
                        className="btn-secondary" style={{ minHeight: 32, padding: '4px 10px', fontSize: 12.5 }}
                        onClick={async () => {
                          if (!current || !user) return
                          await criarTarefa({
                            workspaceId: current.workspace.id,
                            criadorUserId: user.id,
                            dados: {
                              titulo: `Analisar: ${d.nome}`,
                              categoria: 'analisar_exame',
                              prioridade: 'alta',
                              paciente_id: id,
                              responsavel_user_id: user.id,
                            },
                          })
                          window.alert('Pendência criada.')
                        }}
                      >
                        Criar pendência
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {exames.length > 0 && (
            <div className="card" style={{ padding: 16 }}>
              <strong style={{ fontSize: 14.5, display: 'block', marginBottom: 8 }}>Exames solicitados</strong>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {exames.map(ex => (
                  <div key={ex.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}>
                    <span style={{ flex: 1 }}>{ex.nome}</span>
                    <span className={`badge ${ex.status === 'recebido' || ex.status === 'analisado' ? 'badge-ok' : ex.status === 'cancelado' ? 'badge-muted' : 'badge-warn'}`}>
                      {STATUS_EXAME_LABEL[ex.status]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {aba === 'dados' && (
        <div className="card" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
          {([
            ['CPF', paciente.cpf],
            ['Data de nascimento', paciente.data_nasc ? formatarData(paciente.data_nasc) : null],
            ['Gênero', paciente.genero],
            ['Telefone', paciente.telefone],
            ['E-mail', paciente.email],
            ['Observações', paciente.observacoes],
          ] as [string, string | null][]).map(([label, valor]) => (
            <div key={label} style={{ display: 'flex', gap: 10 }}>
              <span style={{ color: 'var(--muted)', minWidth: 150, fontSize: 13 }}>{label}</span>
              <span>{valor || '—'}</span>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 10 }}>
            <span style={{ color: 'var(--muted)', minWidth: 150, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <FileText size={13} aria-hidden /> Cadastrada em
            </span>
            <span>{new Date(paciente.created_at).toLocaleDateString('pt-BR')}</span>
          </div>
        </div>
      )}
    </div>
  )
}
